/**
 * Product information – 2026 PDP layout (<product-v2>).
 *
 * Purpose
 * - Renders the price placeholders ([data-pv2-price], [data-pv2-atc-price]) and the estimated delivery
 *   date ([data-pv2-shipping-date]) for the current variant.
 * - Moves the Recharge subscription app block into the form slot ([data-pv2-subscription-slot]).
 *   Legacy widget (v1, .rc-widget): injects the option content from <template data-pv2-recharge-content>
 *   into its radios. Subscription Widget (v2, <recharge-subscription-widget>, shadow DOM): content and
 *   copy come from the Recharge widget editor; styling uses ::part() in product-v2.css. Elements Recharge
 *   does not expose (benefit items, the benefit <em> pill, the subscription radio) get extra pv2-* part
 *   names, re-applied by MutationObservers whenever Lit re-renders.
 * - Tracks the purchase mode (subscription / onetime) in data-pv2-mode and shows the matching price in
 *   the add to cart button (v1: its purchaseOption radios, v2: the form's selling_plan value).
 * - Opens accordions targeted by "Learn more" links and trims empty trailing lines of the description.
 *
 * Config
 * - Variant data: <script type="application/json" data-pv2-product> inside the element
 *   ({ money_format, variants: [{ id, price, shipping_date, selling_plan_allocations: [{ price }] }] }).
 *
 * Usage
 * - sections/product__main.liquid wraps the section content in <product-v2> and loads this file with
 *   `defer`, only when the "2026 PDP layout" setting (enable_2026_layout) is on. The element initialises
 *   itself when it is connected, so section re-renders in the theme editor need no extra handling.
 */
(() => {
  const RECHARGE_TIMEOUT = 15000;
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MODE_RECHECK_DELAY = 500;
  const DEFAULT_MONEY_FORMAT = '${{amount}}';
  const WIDGET_OBSERVER_OPTIONS = { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] };
  const SHADOW_OBSERVER_OPTIONS = { childList: true, subtree: true };
  const WIDGET_PARTS = {
    benefit: 'pv2-benefit',
    benefitText: 'pv2-benefit-text',
    benefitPill: 'pv2-benefit-pill',
    subscriptionIndicator: 'pv2-subscription-indicator'
  };
  const PURCHASE_OPTION_SELECTOR = '[part~="rc-purchase-option"]';
  // Elements with their own click behaviour inside the widget (the option label, plan dropdown, links).
  const INTERACTIVE_SELECTOR =
    'a, button, input, select, option, textarea, label, summary, [role="button"], [role="link"], [contenteditable="true"]';
  const SUBSCRIPTION_INDICATOR_SELECTOR =
    '[part~="rc-purchase-option__subscription"] [part~="rc-purchase-option__checked-indicator"]';
  const CONTENT_TARGETS = [
    { label: '.rc-radio.subscription-radio', content: 'pv2-rc-content--subscription' },
    { label: '.rc-radio.onetime-radio', content: 'pv2-rc-content--onetime' }
  ];

  /**
   * Formats an amount in cents. Whole amounts are shown without decimals ($499, not $499.00).
   * @param {number} cents - Amount in cents.
   * @param {number} precision - Number of decimals.
   * @param {string} thousands - Thousands separator.
   * @param {string} decimal - Decimal separator.
   * @returns {string} Formatted number without currency.
   */
  const formatWithDelimiters = (cents, precision, thousands, decimal) => {
    if (cents === null || cents === undefined || Number.isNaN(Number(cents))) {
      return '';
    }

    const [whole, fraction] = (Number(cents) / 100).toFixed(precision).split('.');
    const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);

    if (!fraction || Number(fraction) === 0) {
      return grouped;
    }

    return `${grouped}${decimal}${fraction}`;
  };

  /**
   * Checks whether a node is a <br> or a whitespace-only text node (including &nbsp;).
   * @param {Node} node - Node to test.
   * @returns {boolean} True when the node renders as empty space.
   */
  const isBlankNode = (node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      return node.tagName === 'BR';
    }

    return node.nodeType === Node.TEXT_NODE && node.textContent.replace(/ /g, ' ').trim() === '';
  };

  /**
   * Adds a token to a token-list attribute (part: space separated, exportparts: comma separated).
   * Only writes when the token is missing, so Lit bindings and observers are not disturbed.
   * @param {Element} element - Element to update.
   * @param {string} attribute - 'part' or 'exportparts'.
   * @param {string} token - Token to add.
   * @returns {void}
   */
  const addToken = (element, attribute, token) => {
    const separator = attribute === 'exportparts' ? ',' : ' ';
    const current = element.getAttribute(attribute) || '';
    const tokens = current
      .split(separator)
      .map((value) => value.trim())
      .filter(Boolean);

    if (tokens.includes(token)) {
      return;
    }

    tokens.push(token);
    element.setAttribute(attribute, tokens.join(separator === ',' ? ', ' : ' '));
  };

  /**
   * Colour-grouped product gallery (2026 layout).
   *
   * Media belongs to a colour when its alt text names that colour; media that names no colour is shared.
   * For the selected colour the gallery shows: that colour's videos, the variant image, the colour's other
   * media, then the shared media. Everything else waits in a hidden pool, so Flickity's cells and the
   * thumbnails always hold the same media in the same order (the theme selects slides by thumbnail index).
   * Videos play muted on a loop while their slide is selected.
   */
  class ColorGallery {
    /**
     * @param {HTMLElement} root - The <product-v2> element.
     * @param {string[]} colors - The product's colour option values.
     * @param {MutationObserver[]} observers - Shared list, disconnected by <product-v2>.
     */
    constructor(root, colors, observers) {
      this.main = root.querySelector('.product-gallery__main');
      this.thumbnails = root.querySelector('.product-gallery__thumbnails');
      this.colors = colors;
      this.observers = observers;
      this.items = [];
      this.visible = [];
      this.key = null;
      this.flickity = null;

      if (!this.main || !this.colors.length) {
        return;
      }

      this.pool = document.createElement('div');
      this.pool.hidden = true;
      this.main.closest('.product-gallery').appendChild(this.pool);
      this.items = this.collectItems();
      this.bindThumbnails();
      this.bindVideos();
      this.watchFlickity();
    }

    /**
     * Pairs every slide with its thumbnail and colour.
     * @returns {Object[]} Items: { id, slide, thumb, color, isVideo }.
     */
    collectItems() {
      const slides = [...this.main.querySelectorAll('.product-gallery__image[data-media-id]')];

      return slides.map((slide) => {
        const id = slide.getAttribute('data-media-id');
        const thumb = this.thumbnails ? this.thumbnails.querySelector(`[data-media-id="${id}"]`) : null;

        return {
          id,
          slide,
          thumb,
          color: this.matchColor(slide.getAttribute('data-media-alt') || ''),
          isVideo: Boolean(slide.querySelector('video')),
          userPaused: false
        };
      });
    }

    /**
     * Finds the colour named in an alt text (whole word, case-insensitive).
     * @param {string} alt - Media alt text.
     * @returns {string} Lower-cased colour, or '' for shared media.
     */
    matchColor(alt) {
      const text = alt.toLowerCase();
      const match = this.colors.find((color) => {
        const escaped = color.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`).test(text);
      });

      return match ? match.toLowerCase() : '';
    }

    /**
     * Orders the media to show for a colour.
     * @param {string} color - Selected colour (lower case).
     * @param {string|null} featuredId - Media id of the variant image.
     * @returns {Object[]} Items in display order.
     */
    orderFor(color, featuredId) {
      const group = this.items.filter((item) => item.color === color);
      const featured = this.items.find((item) => item.id === featuredId) || null;
      const videos = group.filter((item) => item.isVideo);
      const rest = group.filter((item) => !item.isVideo && item !== featured);
      const shared = this.items.filter((item) => item.color === '' && item !== featured);
      const ordered = [...videos, featured, ...rest, ...shared].filter(Boolean);

      return ordered.length ? ordered : this.items;
    }

    /**
     * Shows the media of a colour, starting with its first item (its video when there is one).
     * @param {string|null} color - Selected colour.
     * @param {number|string|null} featuredId - Media id of the variant image.
     * @returns {void}
     */
    show(color, featuredId) {
      if (!this.items.length) {
        return;
      }

      const colorKey = color ? String(color).toLowerCase() : '';
      const mediaId = featuredId ? String(featuredId) : null;
      const key = `${colorKey}|${mediaId}`;

      if (key === this.key) {
        return;
      }

      this.key = key;
      this.visible = this.orderFor(colorKey, mediaId);

      const hidden = this.items.filter((item) => !this.visible.includes(item));
      const slides = this.visible.map((item) => item.slide);

      this.pauseVideos(hidden);

      if (this.flickity) {
        this.flickity.remove(this.flickity.getCellElements());
        this.flickity.append(slides);
        this.flickity.select(0, false, true);
        this.flickity.resize();
      } else {
        this.main.append(...slides);
      }

      hidden.forEach((item) => this.pool.appendChild(item.slide));
      this.syncThumbnails(hidden);
      this.markSelected(0);
    }

    /**
     * Puts the thumbnails in the same order as the slides and parks the rest.
     * @param {Object[]} hidden - Items that are not shown.
     * @returns {void}
     */
    syncThumbnails(hidden) {
      if (!this.thumbnails) {
        return;
      }

      this.visible.forEach((item) => {
        if (item.thumb) {
          this.thumbnails.appendChild(item.thumb);
        }
      });

      hidden.forEach((item) => {
        if (item.thumb) {
          this.pool.appendChild(item.thumb);
        }
      });

      this.thumbnails.classList.toggle('product-gallery__thumbnails--empty', this.visible.length < 2);
    }

    /**
     * Marks the thumbnail of the selected slide and plays only the selected video.
     * @param {number} index - Selected slide index.
     * @returns {void}
     */
    markSelected(index) {
      this.visible.forEach((item, itemIndex) => {
        const isSelected = itemIndex === index;

        if (item.thumb) {
          item.thumb.classList.toggle('is-nav-selected', isSelected);
        }

        if (!item.isVideo) {
          return;
        }

        if (isSelected) {
          this.playVideo(item);
          return;
        }

        this.pauseVideos([item]);
      });
    }

    /**
     * Plays a slide's video muted (autoplay policies allow muted playback only).
     * @param {Object} item - Gallery item.
     * @returns {void}
     */
    playVideo(item) {
      const video = item.slide.querySelector('video');

      if (!video || item.userPaused || REDUCED_MOTION.matches) {
        return;
      }

      video.muted = true;

      const playing = video.play();

      if (playing && typeof playing.catch === 'function') {
        playing.catch(() => {});
      }
    }

    /**
     * Pauses the videos of the given items.
     * @param {Object[]} items - Gallery items.
     * @returns {void}
     */
    pauseVideos(items) {
      items.forEach((item) => {
        const video = item.isVideo ? item.slide.querySelector('video') : null;

        if (video && !video.paused) {
          video.pause();
        }
      });
    }

    /**
     * Tapping a video toggles it. The pause button added by assets/video-pause.js toggles it too; either way
     * the visitor's choice is remembered, so the gallery does not restart a video they paused.
     * @returns {void}
     */
    bindVideos() {
      this.items.filter((item) => item.isVideo).forEach((item) => {
        const video = item.slide.querySelector('video');

        item.slide.addEventListener('click', (event) => {
          // A swipe ends with a click too; Flickity flags it so it does not count as a tap
          if (this.flickity && this.flickity.isPreventingClicks) {
            return;
          }

          if (!event.target.closest('.a11y-video-toggle')) {
            this.toggleVideo(video);
          }

          item.userPaused = video.paused;
        });
      });
    }

    /**
     * Plays a paused video or pauses a playing one.
     * @param {HTMLVideoElement} video - The video.
     * @returns {void}
     */
    toggleVideo(video) {
      if (!video.paused) {
        video.pause();
        return;
      }

      video.muted = true;

      const playing = video.play();

      if (playing && typeof playing.catch === 'function') {
        playing.catch(() => {});
      }
    }

    /**
     * Thumbnails parked in the pool at load miss the theme's click handlers, so clicks are handled here
     * (by position, which matches the slide order).
     * @returns {void}
     */
    bindThumbnails() {
      if (!this.thumbnails) {
        return;
      }

      const select = (event) => {
        const thumb = event.target.closest('.product-gallery__thumbnail');

        if (!thumb || !this.flickity) {
          return;
        }

        const index = [...this.thumbnails.children].indexOf(thumb);

        if (index >= 0) {
          this.flickity.select(index);
        }
      };

      this.thumbnails.addEventListener('click', select);
      this.thumbnails.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          select(event);
        }
      });
    }

    /**
     * Connects to the theme's Flickity instance once it exists.
     * @returns {void}
     */
    watchFlickity() {
      if (this.connectFlickity()) {
        return;
      }

      const observer = new MutationObserver(() => {
        if (this.connectFlickity()) {
          observer.disconnect();
        }
      });

      observer.observe(this.main, { attributes: true, attributeFilter: ['class'] });
      this.observers.push(observer);
    }

    /**
     * Returns the theme's Flickity instance for the main slider. The theme creates it through its bundled
     * jQuery plugin (on window load), so the instance lives in jQuery data rather than window.Flickity.
     * @returns {Object|null} Flickity instance.
     */
    findFlickity() {
      const fromJquery = window.jQuery ? window.jQuery(this.main).data('flickity') : null;

      if (fromJquery) {
        return fromJquery;
      }

      if (window.Flickity && typeof window.Flickity.data === 'function') {
        return window.Flickity.data(this.main) || null;
      }

      return null;
    }

    /**
     * Stores the Flickity instance and follows its selection.
     * @returns {boolean} True when connected.
     */
    connectFlickity() {
      const instance = this.findFlickity();

      if (!instance) {
        return false;
      }

      this.flickity = instance;
      this.flickity.on('select', () => this.markSelected(this.flickity.selectedIndex));
      this.flickity.select(0, false, true);
      this.markSelected(0);

      return true;
    }
  }

  class ProductV2 extends HTMLElement {
    constructor() {
      super();

      this.data = { money_format: DEFAULT_MONEY_FORMAT, variants: [], colors: [] };
      this.variant = null;
      this.mode = null;
      this.widget = null;
      this.observers = [];
      this.timers = [];
      this.observedRoots = new WeakSet();
      this.initialized = false;
    }

    /**
     * Initialises the element once it is in the document.
     * @returns {void}
     */
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.subscriptionSlot = this.querySelector('[data-pv2-subscription-slot]');
      this.contentTemplate = this.querySelector('template[data-pv2-recharge-content]');
      this.productForm = this.querySelector('form[action*="/cart/add"]');
      this.widgetVersion = 'v1';
      this.readData();
      this.gallery = new ColorGallery(this, this.data.colors || [], this.observers);
      this.moveSubscriptionBlock();
      this.trimDescription();
      this.keepAtcPrice();

      this.addEventListener('click', (event) => this.handleClick(event));

      const picker = this.querySelector('[data-variant-selection]');

      if (picker) {
        picker.addEventListener('variant-change', (event) => this.handleVariantChange(event));
      }

      this.setVariant(this.getInitialVariantId());
      this.waitForRecharge();
    }

    /**
     * Stops observers and timers when the section is removed (theme editor).
     * @returns {void}
     */
    disconnectedCallback() {
      this.observers.forEach((observer) => observer.disconnect());
      this.timers.forEach((timer) => clearTimeout(timer));
      this.observers = [];
      this.timers = [];
      this.observedRoots = new WeakSet();
    }

    /**
     * Reads the variant JSON rendered by product__main.
     * @returns {void}
     */
    readData() {
      const dataElement = this.querySelector('script[data-pv2-product]');

      if (!dataElement) {
        return;
      }

      try {
        const parsed = JSON.parse(dataElement.textContent || '{}');

        if (!parsed || typeof parsed !== 'object') {
          return;
        }

        this.data.money_format = parsed.money_format || DEFAULT_MONEY_FORMAT;
        this.data.variants = Array.isArray(parsed.variants) ? parsed.variants : [];
        this.data.colors = Array.isArray(parsed.colors) ? parsed.colors : [];
      } catch (error) {
        // Malformed JSON: keep the defaults.
      }
    }

    /**
     * Refreshes prices and the delivery date for the current variant. Public, e.g. for app integrations.
     * @returns {void}
     */
    refresh() {
      this.renderPrices();
      this.renderShippingDate();
    }

    /* ---------- Variants ---------- */

    /**
     * Finds a variant in the JSON data.
     * @param {number|string} id - Variant id.
     * @returns {Object|null} Variant data or null.
     */
    findVariant(id) {
      const numericId = Number(id);

      if (!numericId) {
        return null;
      }

      return this.data.variants.find((variant) => Number(variant.id) === numericId) || null;
    }

    /**
     * Returns the variant id selected on page load.
     * @returns {string|number|null} Variant id.
     */
    getInitialVariantId() {
      const picker = this.querySelector('[data-variant-selection]');
      const pickerId = picker ? picker.getAttribute('variant') : null;

      if (pickerId) {
        return pickerId;
      }

      const input = this.querySelector('select[name="id"], input[name="id"]');

      if (input && input.value) {
        return input.value;
      }

      return this.data.variants[0] ? this.data.variants[0].id : null;
    }

    /**
     * Stores the current variant and re-renders. Prefers the JSON data (shipping date + allocations).
     * @param {Object|number|string} variantOrId - Variant object (variant-change event) or id.
     * @returns {void}
     */
    setVariant(variantOrId) {
      const isObject = Boolean(variantOrId) && typeof variantOrId === 'object';
      const id = isObject ? variantOrId.id : variantOrId;
      const fallback = isObject ? variantOrId : null;

      this.variant = this.findVariant(id) || fallback;
      this.refresh();

      if (this.variant && this.gallery) {
        this.gallery.show(this.variant.color, this.variant.featured_media_id);
      }
    }

    /**
     * Handles the variant picker's variant-change event.
     * @param {CustomEvent} event - Event with detail.variant.
     * @returns {void}
     */
    handleVariantChange(event) {
      const variant = event && event.detail ? event.detail.variant : null;

      if (!variant) {
        return;
      }

      this.setVariant(variant);
    }

    /* ---------- Rendering ---------- */

    /**
     * Formats cents with the shop money format.
     * @param {number} cents - Amount in cents.
     * @returns {string} Formatted price.
     */
    formatMoney(cents) {
      if (cents === null || cents === undefined) {
        return '';
      }

      return String(this.data.money_format).replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
        if (key === 'amount_no_decimals') {
          return formatWithDelimiters(cents, 0, ',', '.');
        }

        if (key === 'amount_with_comma_separator') {
          return formatWithDelimiters(cents, 2, '.', ',');
        }

        if (key === 'amount_no_decimals_with_comma_separator') {
          return formatWithDelimiters(cents, 0, '.', ',');
        }

        return formatWithDelimiters(cents, 2, ',', '.');
      });
    }

    /**
     * Returns the subscription price of a variant (first selling plan allocation), or its price.
     * @param {Object|null} variant - Variant data.
     * @returns {number|undefined} Price in cents.
     */
    getSubscriptionPrice(variant) {
      if (!variant) {
        return undefined;
      }

      const allocation = variant.selling_plan_allocations ? variant.selling_plan_allocations[0] : null;

      if (allocation && allocation.price !== null && allocation.price !== undefined) {
        return allocation.price;
      }

      return variant.price;
    }

    /**
     * Sets the text of every matching element when it changed.
     * @param {string} selector - CSS selector inside the element.
     * @param {string} text - New text.
     * @returns {void}
     */
    setText(selector, text) {
      this.querySelectorAll(selector).forEach((element) => {
        if (element.textContent === text) {
          return;
        }

        element.textContent = text;
      });
    }

    /**
     * Renders the option prices and the add to cart price for the current mode.
     * @returns {void}
     */
    renderPrices() {
      const variant = this.variant;

      if (!variant) {
        return;
      }

      const subscriptionPrice = this.getSubscriptionPrice(variant);
      const atcPrice = this.mode === 'subscription' ? subscriptionPrice : variant.price;

      this.setText('[data-pv2-price="onetime"]', this.formatMoney(variant.price));
      this.setText('[data-pv2-price="subscription"]', this.formatMoney(subscriptionPrice));
      this.setText('[data-pv2-atc-price]', this.formatMoney(atcPrice));
    }

    /**
     * Shows the estimated delivery date of the current variant, or hides the line.
     * @returns {void}
     */
    renderShippingDate() {
      const wrapper = this.querySelector('[data-pv2-shipping-date]');

      if (!wrapper) {
        return;
      }

      const date = this.variant ? this.variant.shipping_date : null;

      if (!date) {
        wrapper.hidden = true;
        return;
      }

      const value = wrapper.querySelector('[data-pv2-shipping-date-value]');

      if (value) {
        value.textContent = date;
      }

      wrapper.hidden = false;
    }

    /* ---------- Purchase mode ---------- */

    /**
     * Reads the selected purchase option from the Subscription Widget (v2): a non-empty selling_plan
     * value in the product form means a subscription.
     * @returns {string} 'subscription' or 'onetime'.
     */
    detectModeFromForm() {
      const inputs = this.productForm ? this.productForm.querySelectorAll('[name="selling_plan"]') : [];
      const selected = [...inputs].find((input) => !input.disabled && input.value);

      return selected ? 'subscription' : 'onetime';
    }

    /**
     * Reads the selected purchase option from the Recharge widget.
     * @param {Element} widget - The .rc-widget element (v1) or the <recharge-subscription-widget> host (v2).
     * @returns {string|null} 'subscription', 'onetime' or null.
     */
    detectMode(widget) {
      if (!widget) {
        return null;
      }

      if (this.widgetVersion === 'v2') {
        return this.detectModeFromForm();
      }

      const checked = widget.querySelector('input[name="purchaseOption"]:checked');

      if (checked && checked.value) {
        return checked.value === 'subscription' ? 'subscription' : 'onetime';
      }

      const active = widget.querySelector('.rc-radio.rc-radio--active');

      if (!active) {
        return null;
      }

      return active.classList.contains('subscription-radio') ? 'subscription' : 'onetime';
    }

    /**
     * Updates data-pv2-mode and the add to cart price when the purchase option changed.
     * @returns {void}
     */
    updateMode() {
      const mode = this.detectMode(this.widget);

      if (!mode || mode === this.mode) {
        return;
      }

      this.mode = mode;
      this.setAttribute('data-pv2-mode', mode);
      this.renderPrices();
    }

    /* ---------- Recharge ---------- */

    /**
     * Moves the Recharge app block into the form slot (between the options and add to cart).
     * Runs before the Recharge module script mounts, as both are deferred and this file comes first.
     * @returns {void}
     */
    moveSubscriptionBlock() {
      if (!this.subscriptionSlot || this.subscriptionSlot.firstElementChild) {
        return;
      }

      // Both widget versions render a .recharge-subscription-widget app block; v2 contains the custom element.
      const widgetV2 = this.querySelector('.product-app recharge-subscription-widget');
      const appBlock = widgetV2 || this.querySelector('.product-app .recharge-subscription-widget');
      const block = appBlock ? appBlock.closest('.product-block') : null;

      if (!block) {
        return;
      }

      block.classList.add('pv2-subscription__block');
      this.subscriptionSlot.appendChild(block);
    }

    /**
     * Clones one option content element from the template.
     * @param {string} className - Class of the content element.
     * @returns {Element|null} Clone or null.
     */
    cloneContent(className) {
      const source = this.contentTemplate ? this.contentTemplate.content.querySelector(`.${className}`) : null;

      return source ? source.cloneNode(true) : null;
    }

    /**
     * Appends the option content to Recharge radios that do not have it yet.
     * @param {Element} widget - The .rc-widget element.
     * @returns {boolean} True when the DOM changed.
     */
    injectContent(widget) {
      let changed = false;

      CONTENT_TARGETS.forEach(({ label, content }) => {
        widget.querySelectorAll(label).forEach((radio) => {
          if (radio.querySelector('.pv2-rc-content')) {
            return;
          }

          const clone = this.cloneContent(content);

          if (!clone) {
            return;
          }

          radio.appendChild(clone);
          changed = true;
        });
      });

      if (!widget.classList.contains('pv2-rc-enhanced')) {
        widget.classList.add('pv2-rc-enhanced');
        changed = true;
      }

      return changed;
    }

    /**
     * Re-applies the content and mode after Recharge (Vue) re-renders.
     * @param {MutationObserver} observer - The widget observer.
     * @returns {void}
     */
    syncWidget(observer) {
      observer.disconnect();

      if (!this.widget || !this.widget.isConnected) {
        this.widget = this.subscriptionSlot.querySelector('.rc-widget');
      }

      if (!this.widget) {
        observer.observe(this.subscriptionSlot, WIDGET_OBSERVER_OPTIONS);
        return;
      }

      if (this.injectContent(this.widget)) {
        this.renderPrices();
      }

      this.updateMode();
      observer.observe(this.subscriptionSlot, WIDGET_OBSERVER_OPTIONS);
    }

    /**
     * Enhances the rendered Recharge widget and keeps it in sync.
     * @param {Element} widget - The .rc-widget element.
     * @returns {void}
     */
    enhanceWidget(widget) {
      const observer = new MutationObserver(() => this.syncWidget(observer));

      this.widget = widget;
      this.observers.push(observer);
      this.subscriptionSlot.setAttribute('data-state', 'ready');
      this.syncWidget(observer);

      this.subscriptionSlot.addEventListener('change', (event) => {
        if (!event.target || event.target.name !== 'purchaseOption') {
          return;
        }

        this.updateMode();
      });

      // Vue may set the checked state programmatically after the first paint.
      this.timers.push(setTimeout(() => this.updateMode(), MODE_RECHECK_DELAY));
    }

    /**
     * Waits for the Recharge widget to render its radios. If nothing rendered after a timeout, the
     * label + slot are hidden until the widget shows up (slow network) or for good (no widget).
     * @returns {void}
     */
    waitForRecharge() {
      if (!this.subscriptionSlot) {
        return;
      }

      const widgetV2 = this.subscriptionSlot.querySelector('recharge-subscription-widget');

      if (widgetV2) {
        this.waitForWidgetV2(widgetV2);
        return;
      }

      const rendered = this.subscriptionSlot.querySelector('.rc-widget .rc-radio');

      if (rendered) {
        this.enhanceWidget(rendered.closest('.rc-widget'));
        return;
      }

      const wrapper = this.querySelector('[data-pv2-subscription]');
      const observer = new MutationObserver(() => {
        const radio = this.subscriptionSlot.querySelector('.rc-widget .rc-radio');

        if (!radio) {
          return;
        }

        observer.disconnect();
        clearTimeout(timer);

        if (wrapper) {
          wrapper.hidden = false;
        }

        this.enhanceWidget(radio.closest('.rc-widget'));
      });

      const timer = setTimeout(() => {
        this.subscriptionSlot.setAttribute('data-state', 'ready');

        if (!wrapper || this.subscriptionSlot.querySelector('.rc-widget')) {
          return;
        }

        wrapper.hidden = true;
      }, RECHARGE_TIMEOUT);

      this.observers.push(observer);
      this.timers.push(timer);
      observer.observe(this.subscriptionSlot, { childList: true, subtree: true });
    }

    /**
     * Checks whether the Subscription Widget (v2) has rendered anything. It stays empty when no widget is
     * published in Recharge for this product template.
     * @param {Element} host - The <recharge-subscription-widget> element.
     * @returns {boolean} True when the widget has content.
     */
    isWidgetV2Rendered(host) {
      if (host.shadowRoot && host.shadowRoot.childElementCount > 0) {
        return host.getBoundingClientRect().height > 0;
      }

      return false;
    }

    /**
     * Wires the Subscription Widget (v2): keeps data-pv2-mode and the add to cart price in sync with the
     * selected plan, and hides the label + slot when no widget renders.
     * @param {Element} host - The <recharge-subscription-widget> element.
     * @returns {void}
     */
    waitForWidgetV2(host) {
      const wrapper = this.querySelector('[data-pv2-subscription]');

      this.widgetVersion = 'v2';
      this.widget = host;
      this.setAttribute('data-pv2-widget', 'v2');

      // Clicks and input events are composed, so they reach the host from inside the shadow DOM.
      host.addEventListener('click', (event) => this.selectOptionFromCard(event));
      host.addEventListener('click', () => this.timers.push(setTimeout(() => this.updateMode(), 0)));
      host.addEventListener('input', () => this.updateMode());

      if (this.productForm) {
        const formObserver = new MutationObserver(() => this.updateMode());

        formObserver.observe(this.productForm, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['value', 'disabled']
        });
        this.observers.push(formObserver);
        this.productForm.addEventListener('change', () => this.updateMode());
      }

      const showWhenRendered = () => {
        if (!this.isWidgetV2Rendered(host)) {
          return;
        }

        this.subscriptionSlot.setAttribute('data-state', 'ready');

        if (wrapper) {
          wrapper.hidden = false;
        }

        this.observeWidgetParts(host);
        this.updateMode();
      };

      const resizeObserver = new ResizeObserver(showWhenRendered);

      resizeObserver.observe(host);
      this.observers.push(resizeObserver);

      const timer = setTimeout(() => {
        this.subscriptionSlot.setAttribute('data-state', 'ready');

        if (wrapper && !this.isWidgetV2Rendered(host)) {
          wrapper.hidden = true;
        }
      }, RECHARGE_TIMEOUT);

      this.timers.push(timer);
    }

    /**
     * Makes the whole purchase option card clickable. Recharge only wraps the title row in a <label>, so
     * clicks on the card padding, benefits or drawbacks select nothing. Such clicks select the card's radio
     * through a native click, the same path as a click on the title, so Recharge keeps handling the plan,
     * selling_plan input and prices. Clicks on interactive elements (the label itself, the plan dropdown,
     * links) are left alone, and the synthetic click is ignored when it comes back (its path has the input).
     * The listener sits on the host, so it survives Lit re-renders (e.g. on variant change).
     * @param {MouseEvent} event - Click event on the <recharge-subscription-widget> host.
     * @returns {void}
     */
    selectOptionFromCard(event) {
      if (event.defaultPrevented || event.button !== 0) {
        return;
      }

      let card = null;

      for (const node of event.composedPath()) {
        if (node === event.currentTarget) {
          break;
        }

        if (!(node instanceof Element)) {
          continue;
        }

        if (node.matches(INTERACTIVE_SELECTOR)) {
          return;
        }

        if (node.matches(PURCHASE_OPTION_SELECTOR)) {
          card = node;
          break;
        }
      }

      const input = card ? card.querySelector('input[name="purchaseOption"]') : null;

      if (!input || input.checked || input.disabled) {
        return;
      }

      // Keep text selection inside the card (drag to copy) from switching the option.
      const selection = window.getSelection ? window.getSelection() : null;

      if (selection && !selection.isCollapsed) {
        return;
      }

      input.click();
    }

    /**
     * Watches a shadow root once and re-applies the theme's part names whenever Lit re-renders it.
     * @param {ShadowRoot} root - Shadow root to observe.
     * @param {Element} host - The <recharge-subscription-widget> element.
     * @returns {void}
     */
    observeShadowRoot(root, host) {
      if (!root || this.observedRoots.has(root)) {
        return;
      }

      const observer = new MutationObserver(() => this.applyWidgetParts(host));

      observer.observe(root, SHADOW_OBSERVER_OPTIONS);
      this.observedRoots.add(root);
      this.observers.push(observer);
    }

    /**
     * Starts tagging the Subscription Widget (v2) once its shadow root exists.
     * @param {Element} host - The <recharge-subscription-widget> element.
     * @returns {void}
     */
    observeWidgetParts(host) {
      if (!host.shadowRoot) {
        return;
      }

      this.observeShadowRoot(host.shadowRoot, host);
      this.applyWidgetParts(host);
    }

    /**
     * Adds theme part names inside the widget's shadow DOM, which Recharge does not expose:
     * - the subscription option's radio indicator (hidden on mobile),
     * - each benefit <li>, its <p> and its <em> (the "20% off" pill), forwarded through <rc-benefits>'
     *   exportparts.
     * @param {Element} host - The <recharge-subscription-widget> element.
     * @returns {void}
     */
    applyWidgetParts(host) {
      const root = host.shadowRoot;

      if (!root) {
        return;
      }

      root.querySelectorAll(SUBSCRIPTION_INDICATOR_SELECTOR).forEach((indicator) => {
        addToken(indicator, 'part', WIDGET_PARTS.subscriptionIndicator);
      });

      root.querySelectorAll('rc-benefits').forEach((benefits) => {
        addToken(benefits, 'exportparts', WIDGET_PARTS.benefit);
        addToken(benefits, 'exportparts', WIDGET_PARTS.benefitText);
        addToken(benefits, 'exportparts', WIDGET_PARTS.benefitPill);
        this.tagBenefits(benefits, host);
      });
    }

    /**
     * Tags the benefit items and pills of one <rc-benefits> element and watches its own shadow root.
     * @param {Element} benefits - The <rc-benefits> element.
     * @param {Element} host - The <recharge-subscription-widget> element.
     * @returns {void}
     */
    tagBenefits(benefits, host) {
      const root = benefits.shadowRoot;

      if (!root) {
        // Not rendered yet: retry once Lit has finished its first update.
        if (benefits.updateComplete) {
          benefits.updateComplete.then(() => this.applyWidgetParts(host));
        }

        return;
      }

      this.observeShadowRoot(root, host);
      root.querySelectorAll('li').forEach((item) => addToken(item, 'part', WIDGET_PARTS.benefit));
      root.querySelectorAll('li > p').forEach((text) => addToken(text, 'part', WIDGET_PARTS.benefitText));
      root.querySelectorAll('em').forEach((pill) => addToken(pill, 'part', WIDGET_PARTS.benefitPill));
    }

    /* ---------- Description, links, add to cart ---------- */

    /**
     * "Learn more" links pointing at an accordion (#pv2-…) open it before scrolling to it.
     * @param {MouseEvent} event - Click event.
     * @returns {void}
     */
    handleClick(event) {
      const link = event.target.closest ? event.target.closest('a[href^="#pv2-"]') : null;

      if (!link) {
        return;
      }

      const target = this.querySelector(link.getAttribute('href'));

      if (!target) {
        return;
      }

      event.preventDefault();

      if (target.tagName === 'DETAILS') {
        target.open = true;
      }

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /**
     * Product descriptions often end with "&nbsp;<br><br>", which adds an empty line before "Learn more".
     * Removes trailing blank nodes and hides paragraphs left empty.
     * @returns {void}
     */
    trimDescription() {
      this.querySelectorAll('.product-block--description p').forEach((paragraph) => {
        let node = paragraph.lastChild;

        while (node && isBlankNode(node)) {
          const previous = node.previousSibling;

          node.remove();
          node = previous;
        }

        if (paragraph.textContent.replace(/ /g, ' ').trim() !== '') {
          return;
        }

        paragraph.hidden = true;
      });
    }

    /**
     * The ajax cart resets the button text to data-label after an error, dropping the "- $499" price.
     * Re-adds the price element whenever it disappears.
     * @returns {void}
     */
    keepAtcPrice() {
      const atcPrice = this.querySelector('.button--add-to-cart .pv2-atc-price');

      if (!atcPrice) {
        return;
      }

      const label = atcPrice.parentNode;
      const priceTemplate = atcPrice.cloneNode(true);
      const observer = new MutationObserver(() => {
        if (label.querySelector('.pv2-atc-price')) {
          return;
        }

        label.appendChild(priceTemplate.cloneNode(true));
        this.renderPrices();
      });

      this.observers.push(observer);
      observer.observe(label, { childList: true });
    }
  }

  if (!customElements.get('product-v2')) {
    customElements.define('product-v2', ProductV2);
  }
})();
